import designSystem from "./PGAProductDesignSystem.json";

const themes = designSystem.find((t) => t.name === "Themes");
const lightTheme = themes?.values.find((t) => t.mode.name === "Light");
const darkTheme = themes?.values.find((t) => t.mode.name === "Dark");

type ThemeValue = NonNullable<typeof lightTheme>;
type ColorMap = { [key: string]: string };

function getColorsFromTheme(theme: ThemeValue | undefined, prefix: string): ColorMap {
  return (
    theme?.color
      .filter((c) => c.name.startsWith(prefix))
      .reduce<ColorMap>((acc, entry) => {
        acc[entry.name.toLowerCase().replace(/\//g, "-")] = entry.color;
        return acc;
      }, {}) || {}
  );
}

function mergeWithLightFallback(darkColors: ColorMap, lightColors: ColorMap): ColorMap {
  return { ...lightColors, ...darkColors };
}

export const sysColors = getColorsFromTheme(lightTheme, "sys/brand");
export const stateLayersColors = getColorsFromTheme(lightTheme, "state-layers/brand");
export const extendedColors = getColorsFromTheme(lightTheme, "extended");

export const darkSysColors = mergeWithLightFallback(getColorsFromTheme(darkTheme, "sys/brand"), sysColors);
export const darkStateLayersColors = mergeWithLightFallback(
  getColorsFromTheme(darkTheme, "state-layers/brand"),
  stateLayersColors
);
export const darkExtendedColors = mergeWithLightFallback(getColorsFromTheme(darkTheme, "extended"), extendedColors);

function materialWebVariablesFrom(sys: ColorMap): ColorMap {
  return Object.keys(sys).reduce<ColorMap>((acc, key) => {
    acc[`--md-sys-color-${key.replace("sys-brand-", "")}`] = sys[key];
    return acc;
  }, {});
}

export const materialWebColorVariables = materialWebVariablesFrom(sysColors);
export const darkMaterialWebColorVariables = materialWebVariablesFrom(darkSysColors);

const OPAQUE_HEX = /^#[0-9A-Fa-f]{6}$/;

function hexToRgbChannels(hex: string): string | null {
  if (!OPAQUE_HEX.test(hex)) return null;
  const value = hex.slice(1);
  const r = Number.parseInt(value.slice(0, 2), 16);
  const g = Number.parseInt(value.slice(2, 4), 16);
  const b = Number.parseInt(value.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

function colorCustomPropertiesFrom(...maps: ColorMap[]): ColorMap {
  const merged = Object.assign({}, ...maps) as ColorMap;
  return Object.fromEntries(
    Object.entries(merged).map(([key, value]) => [`--color-${key}`, hexToRgbChannels(value) ?? value])
  );
}

export const lightColorCustomProperties = colorCustomPropertiesFrom(sysColors, stateLayersColors, extendedColors);
export const darkColorCustomProperties = colorCustomPropertiesFrom(
  darkSysColors,
  darkStateLayersColors,
  darkExtendedColors
);

function toCssVarColor(key: string, hex: string): string {
  return OPAQUE_HEX.test(hex) ? `rgb(var(--color-${key}) / <alpha-value>)` : `var(--color-${key})`;
}

function toTailwindCssVarColors(colors: ColorMap): ColorMap {
  return Object.fromEntries(Object.entries(colors).map(([key, hex]) => [key, toCssVarColor(key, hex)]));
}

export const tailwindThemeColors = {
  ...toTailwindCssVarColors(sysColors),
  ...toTailwindCssVarColors(stateLayersColors),
  ...toTailwindCssVarColors(extendedColors)
};
